import { type Carer, type HomeDto, UserRole } from '@lmc/ts-common'
import { Button, CollapsibleItem, Dropdown, Edit, Tag } from '@lmc/ui-kit'
import type { ToggleCollapseFn } from '@lmc/ui-kit/src/dataDisplay/CollapsibleItem/CollapsibleItem'
import DropdownIcon from '@lmc/ui-kit/src/dataDisplay/ListItem/DropdownIcon'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Divider, Tooltip } from 'antd'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import { lowerFirst } from 'lodash'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import {
  ALLOCATION_LIST_QUERY_KEY,
  REVISION_QUERY_KEY,
  REVISIONS_QUERY_KEY
} from 'screens/Rostering/hooks/queryConstants'
import type { ClockInOutType } from 'screens/Rostering/hooks/useClockInOut'
import useRevisionUpdate from 'screens/Rostering/hooks/useRevisionUpdate'
import { WarningIcon } from 'screens/Rostering/RotaBuilder/common/components/icons'
import WarningPopover from 'screens/Rostering/RotaBuilder/common/components/popovers/WarningPopover'
import AllocationWindow from 'screens/Rostering/RotaBuilder/screens/AllocationWindow/AllocationWindow'
import SkillsToMatchContainer from 'screens/Rostering/RotaBuilder/screens/AllocationWindow/components/SkillsToMatchContainer'
import {
  type ClockInTeamMember,
  RotaInstanceStatus
} from 'screens/Rostering/types'
import styled from 'styled-components'
import { ClockInOutDetails } from './DrawerStatus/ClockInOutDetails'
import { ClockInOutModal } from './DrawerStatus/ClockInOutModal'
import { Status } from './DrawerStatus/Status'
import EditReqModal from './EditReqModal'
import { MissingAllocationListItem } from './MissingAllocationListItem'
import TeamMemberInfo from './TeamMemberInfo'
import TravelDetails from './TravelDetails'

export type InstanceWarningMaps = Record<string, { notYetClockedIn?: string[] }>

type DrawerAllocatedTeamProps = {
  revisionDetail: any
  publishedRota?: boolean
  instanceWarningMaps?: InstanceWarningMaps | null
}

export const DrawerAllocatedTeam: FC<DrawerAllocatedTeamProps> = ({
  revisionDetail,
  publishedRota,
  instanceWarningMaps
}) => {
  const { t } = useTranslation()

  const [allocationWindowVisible, setAllocationWindowVisible] = useState(false)
  const [editReqModalVisible, setEditReqModalVisible] = useState(false)

  const [clockInOutType, setClockInOutType] = useState<ClockInOutType>('out')
  const [clockInOutAction, setClockInOutAction] = useState<'edit' | 'manual'>(
    'manual'
  )
  const [teamMember, setTeamMember] = useState<ClockInTeamMember>()
  const [isClockInOutModalOpen, setIsClockInOutModalOpen] = useState(false)

  const queryClient = useQueryClient()

  const isDeletedSU = !!revisionDetail?.deletedServiceUser

  const isDraftEvent = revisionDetail?.status === RotaInstanceStatus.DRAFT

  const { updateRevision, isLoading: isLoadingUpdate } = useRevisionUpdate({
    onUpdateSuccess: () => {
      queryClient.invalidateQueries([ALLOCATION_LIST_QUERY_KEY])
      queryClient.invalidateQueries([REVISION_QUERY_KEY, revisionDetail?.id])
      queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
    }
  })

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const { hasRota } = useHomeType()
  const geofencingFlags = useFeatureFlags({ flagNames: ['geofencing'] })?.data
  const geofencingEnabled = geofencingFlags?.geofencing?.enabled

  const address = revisionDetail?.address?.find(
    (item: { id: string }) => item.id === revisionDetail?.addressId
  )
  const isGeofencingEnabled =
    (hasRota ?? false) &&
    (home?.rotaSettings as { geofencing?: boolean } | undefined)?.geofencing ===
      true &&
    geofencingEnabled === true
  const isAddressInvalidForGeofencing = address
    ? isGeofencingEnabled &&
      (address.notValidForGeofencing === true ||
        address.lat == null ||
        address.lng == null)
    : false

  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )

  const isCareHomeAdmin = userRole === UserRole.careHomeAdmin
  const isSeniorCarer = userRole === UserRole.seniorCarer

  const flags = useFeatureFlags({
    flagNames: [
      'rostering_editing_limitations',
      'rota_admin_team_member_edit',
      'rota_warnings_email_notifications'
    ]
  })?.data

  const flagsRosteringEditingLimitations =
    flags?.rostering_editing_limitations?.enabled

  const flagsRotaAdminTeamMemberEdit =
    flags?.rota_admin_team_member_edit?.enabled

  const flagsRotaWarningsEmailNotifications =
    flags?.rota_warnings_email_notifications?.enabled

  const warningsTolerance = home.rotaSettings?.warningsTolerance

  const notYetClockedInTeamMemberIds =
    instanceWarningMaps?.[revisionDetail?.id]?.notYetClockedIn ?? []

  if (!revisionDetail) {
    return null
  }

  const allocatedCount = revisionDetail?.teamMembers?.length || 0

  const missingAllocationsNumber =
    Number(revisionDetail?.numberSupply) - allocatedCount

  const missingAllocations = Array.from({ length: missingAllocationsNumber })

  const hasMissingAllocations = !!missingAllocations?.length

  const isDone = revisionDetail?.status === RotaInstanceStatus.DONE

  const isCancelled = revisionDetail?.status === RotaInstanceStatus.CANCELLED

  const canEditByAdmin =
    flagsRotaAdminTeamMemberEdit &&
    isCareHomeAdmin &&
    isDone &&
    !isDeletedSU &&
    !isCancelled

  const isEditable =
    !publishedRota &&
    (flagsRosteringEditingLimitations
      ? !isDeletedSU && (!isDone || canEditByAdmin) && !isCancelled
      : true)

  const shouldShowEditButton =
    isEditable ||
    (isDone &&
      flagsRotaAdminTeamMemberEdit &&
      (isCareHomeAdmin || isSeniorCarer))

  const isEditButtonDisabled =
    isDone && flagsRotaAdminTeamMemberEdit && isSeniorCarer && !isCareHomeAdmin

  const displayStatus = flagsRosteringEditingLimitations
    ? !isDraftEvent && !isCancelled
    : true

  const getWarnings = (data: {
    allocated: unknown[]
    absences: unknown[]
    skills: { areAllMatched: boolean }
    name: { first: string; last: string }
    teamMemberId?: string
  }) => {
    const warnings: string[] = []

    const name = `${data?.name?.first} ${data?.name?.last}`
    if (data?.allocated?.length) {
      warnings.push(t('NAME_NOT_FULLY_MATCHED', { var: name }))
    }

    if (data?.absences?.length) {
      warnings.push(t('ABSENCES_NOT_FULLY_MATCHED', { var: name }))
    }

    if (data?.skills?.areAllMatched === false) {
      warnings.push(t('SKILLS_NOT_FULLY_MATCHED'))
    }

    if (
      flagsRotaWarningsEmailNotifications &&
      data?.teamMemberId &&
      notYetClockedInTeamMemberIds.includes(data.teamMemberId)
    ) {
      warnings.push(t('NOT_YET_CLOCKED_IN'))
    }

    return warnings
  }

  const openClockInOutModal = ({
    action,
    type,
    teamMember
  }: {
    action: 'edit' | 'manual'
    type: ClockInOutType
    teamMember: ClockInTeamMember
  }) => {
    setIsClockInOutModalOpen(true)
    setClockInOutType(type)
    setClockInOutAction(action)
    setTeamMember(teamMember)
  }

  const closeClockInOutModal = () => setIsClockInOutModalOpen(false)

  const handleEditClick = ({ key }: { key: string }) => {
    if (key === 'editEvent') {
      setEditReqModalVisible(true)
    } else if (key === 'editPattern') {
      const id = revisionDetail?.patternId
      const type = revisionDetail?.siteShift ? 'shift' : 'visit'

      if (id) {
        window.open(`${window.location.origin}/rota/${id}/${type}`, '_blank')
      }
    }
  }

  const editMenuItems = [
    {
      key: 'editEvent',
      label: t('EDIT_THIS_EVENT')
    },
    {
      key: 'editPattern',
      label: t('EDIT_PATTERN')
    }
  ]

  const getMatchedCount = () => {
    const { plannedDuration, clockIns, numberSupply = 0 } = revisionDetail || {}
    const tolerance = warningsTolerance?.durationMismatched || 0

    if (!plannedDuration || !clockIns) {
      return `0/${numberSupply}`
    }

    const matchedCount = clockIns.reduce(
      (count: number, clockIn: { actualDuration: number }) => {
        const durationDiff = Math.abs(clockIn.actualDuration - plannedDuration)
        return durationDiff <= tolerance ? count + 1 : count
      },
      0
    )

    return `${matchedCount}/${numberSupply}`
  }

  return (
    <>
      <DetailsWrapper>
        <HeadingWrapper>
          <Heading level={3}>{t('TEAM_DETAILS')}</Heading>
          {shouldShowEditButton && (
            <Tooltip
              title={
                isEditButtonDisabled
                  ? t('TEAM_MEMBER_EDIT_ADMIN_ONLY_TOOLTIP', {
                      defaultValue:
                        'This can only be edited by admins in the current status of the event.'
                    })
                  : undefined
              }
            >
              <Dropdown
                menu={{
                  items: editMenuItems,
                  onClick: handleEditClick
                }}
                trigger={['click']}
                disabled={isEditButtonDisabled}
              >
                <Button variant="text" disabled={isEditButtonDisabled}>
                  <Edit />
                  {t('EDIT')}
                </Button>
              </Dropdown>
            </Tooltip>
          )}
        </HeadingWrapper>
        <GrayWrapper>
          <HeadingWrapperMain>
            <StyledHeading level={5} weight="bold">
              {t('QUALITY_INDICATORS')}
            </StyledHeading>
          </HeadingWrapperMain>
          <InfoSectionWrapperRow>
            <InfoSection>
              <StyledText level={2}>{t('TEAM_MEMBERS_REQUIRED')}</StyledText>
              <AllocatedText>
                <Text weight="bold">{`${
                  revisionDetail?.teamMembers?.length || 0
                }/${revisionDetail?.numberSupply || 0}`}</Text>
                <Text>{lowerFirst(t('ALLOCATED'))}</Text>
              </AllocatedText>
            </InfoSection>

            <SkillsSection>
              <StyledText level={2}>{t('SKILLS_REQUIRED')}</StyledText>
              <Text weight="bold">
                <SkillsToMatchContainer
                  skillsIds={revisionDetail?.skills}
                  withColor={!isCancelled}
                />
              </Text>
            </SkillsSection>

            <InfoSection>
              <StyledText level={2}>{t('MATCHED_DURATION')}</StyledText>
              <AllocatedText>
                <Text weight="bold">{getMatchedCount()}</Text>
              </AllocatedText>
            </InfoSection>
          </InfoSectionWrapperRow>
        </GrayWrapper>

        {revisionDetail.teamMembers?.map((item: any) => {
          const warnings = getWarnings(item)
          const skills = item?.skills?.skillDetails

          return (
            <CollapsibleSection
              header={(
                toggleCollapse: ToggleCollapseFn,
                collapsed: boolean
              ) => (
                <Wrapper>
                  <TeamMemberInfo teamMember={item?.carer} />
                  <RightContainer>
                    {!!warnings?.length && (
                      <WarningPopover warningsData={warnings}>
                        <WarningIconWrapper>
                          <WarningIcon />
                        </WarningIconWrapper>
                      </WarningPopover>
                    )}
                    {displayStatus && (
                      <Status
                        revisionDetail={revisionDetail}
                        teamMember={item}
                      />
                    )}
                    {toggleCollapse && (
                      <IconButton onClick={toggleCollapse}>
                        <RotatedDropdownIcon $isCollapsed={collapsed} />
                      </IconButton>
                    )}
                  </RightContainer>
                </Wrapper>
              )}
              defaultCollapsed
            >
              <CollapseWrapper>
                <SectionWrapper>
                  <StyledDivider />
                  <StyledSectionTitleText level={2}>
                    {t('SKILLS_MATCHED')}
                  </StyledSectionTitleText>
                  <StyledSkillsContainer>
                    {!!skills?.length &&
                      skills?.map(
                        (item: { name: string; selected: boolean }) => (
                          <StyledTag
                            color={item?.selected ? 'green' : 'orange'}
                          >
                            <Text weight="medium" level={2}>
                              {item.name}
                            </Text>
                          </StyledTag>
                        )
                      )}
                    {!skills?.length && (
                      <StyledLightText>
                        {t('NO_SKILLS_SELECTED')}
                      </StyledLightText>
                    )}
                  </StyledSkillsContainer>
                </SectionWrapper>

                <TravelDetails
                  teamMember={item}
                  teamMemberId={item?.teamMemberId}
                  revisionDetail={revisionDetail}
                />

                <ClockInOutDetails
                  revisionDetail={revisionDetail}
                  teamMember={item}
                  openClockInOutModal={openClockInOutModal}
                  publishedRota={publishedRota}
                  isAddressInvalidForGeofencing={isAddressInvalidForGeofencing}
                />
              </CollapseWrapper>
            </CollapsibleSection>
          )
        })}
        {hasMissingAllocations &&
          missingAllocations?.map(() => (
            <MissingAllocationListItem
              openAllocationWindow={
                isEditable ? () => setAllocationWindowVisible(true) : undefined
              }
            />
          ))}
      </DetailsWrapper>

      <AllocationWindow
        allocationWindowVisible={allocationWindowVisible}
        onCancel={() => setAllocationWindowVisible(false)}
        revisionDetail={revisionDetail}
      />
      <EditReqModal
        editModalVisible={editReqModalVisible}
        onCancel={() => setEditReqModalVisible(false)}
        revisionDetail={revisionDetail}
        updateRevision={updateRevision}
        isLoadingUpdate={isLoadingUpdate}
      />

      {isClockInOutModalOpen && (
        <ClockInOutModal
          open={isClockInOutModalOpen}
          onClose={closeClockInOutModal}
          revisionDetail={revisionDetail}
          action={clockInOutAction}
          type={clockInOutType}
          teamMember={teamMember}
        />
      )}
    </>
  )
}

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  color: ${(props) => props.theme.color.neutral.color3};
`

const HeadingWrapper = styled.div`
  display: flex;
  margin-top: 8px;
  align-items: center;
  justify-content: space-between;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 250px;
`

const AllocatedText = styled.div`
  display: flex;
  gap: 4px;
`

const StyledDivider = styled(Divider)`
  margin: 0;
`
const SkillsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const GrayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  background-color: ${(props) => props.theme.color.neutral.color10};
  border-radius: 20px;
`

const InfoSectionWrapperRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
`

const CollapsibleSection = styled(CollapsibleItem)`
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  border-radius: 16px;
`

const IconButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;
  width: 40px;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    width: 24px;
    margin: 0;
  }
`

const RotatedDropdownIcon = styled(DropdownIcon)<{ $isCollapsed?: boolean }>`
  transform: ${(props) =>
    props.$isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'};
  transition: transform 0.3s ease-in-out;
`

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px;
`

const CollapseWrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledHeading = styled(Heading)`
  font-size: 20px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const HeadingWrapperMain = styled.div`
  display: flex;
  justify-content: space-between;
`

const StyledSectionTitleText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledTag = styled(Tag)`
  width: min-content;
`

const WarningIconWrapper = styled.div`
  cursor: pointer;
  margin-left: auto;
  margin-right: 0px;
  margin-top: 6px;
`

const StyledLightText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  white-space: nowrap;
`

const StyledSkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`
const RightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
